import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, ArrowRight, Rocket, Clock, CreditCard, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { statusLabel } from "../data/previewStatus";
import { previewCheckout } from "../lib/api";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
};

const REQUEST_STATE = {
  in_review: {
    label: "In Review",
    badge: "bg-blue-50 text-blue-600",
    icon: Clock,
    message: "Our team is reviewing your request. We'll notify you here once it's approved.",
  },
  accepted: {
    label: "Approved",
    badge: "bg-emerald-50 text-emerald-600",
    icon: CreditCard,
    message: "Great news — your request was approved! Secure your payment to start your preview.",
  },
  declined: {
    label: "Not Approved",
    badge: "bg-red-50 text-red-600",
    icon: XCircle,
    message: "This request wasn't approved. Feel free to submit a new request with more detail.",
  },
};

export const RequestCard = ({ request }) => {
  const [loading, setLoading] = useState(false);
  const state = REQUEST_STATE[request.status] || REQUEST_STATE.in_review;
  const Icon = state.icon;

  const pay = async () => {
    setLoading(true);
    try {
      const { url } = await previewCheckout(request.id);
      window.location.href = url;
      setTimeout(() => setLoading(false), 6000);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div
      className="bg-white rounded-2xl p-6 border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col"
      data-testid={`preview-request-card-${request.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-lg font-bold text-[#0F0F0F] truncate">{request.project_name}</h3>
          <p className="text-xs text-[#687280] mt-0.5">
            {request.app_category ? `${request.app_category} · ` : ""}Submitted {fmtDate(request.created_at)}
          </p>
        </div>
        <span className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${state.badge}`} data-testid={`request-status-${request.id}`}>
          <Icon size={12} /> {state.label}
        </span>
      </div>

      <p className="text-sm text-[#687280] mt-4 flex-1">{state.message}</p>

      {request.status === "accepted" && (
        <button
          onClick={pay}
          disabled={loading}
          className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full py-3 text-sm font-semibold hover:bg-[#c5a030] transition-colors disabled:opacity-60"
          data-testid={`request-pay-btn-${request.id}`}
        >
          {loading ? <Loader2 className="animate-spin" size={16} /> : <>Continue to Secure Payment · $250 <ArrowRight size={15} /></>}
        </button>
      )}
      {request.status === "in_review" && (
        <div className="mt-5 h-1.5 rounded-full bg-[#F0F0F0] overflow-hidden">
          <div className="h-full w-1/3 bg-blue-400 rounded-full animate-pulse" />
        </div>
      )}
    </div>
  );
};

export const PreviewProjectCard = ({ project }) => {
  const navigate = useNavigate();
  const pct = project.progress_percentage || 0;
  const previewReady = !!project.preview_url;

  return (
    <div
      className="bg-white rounded-2xl p-6 border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
      data-testid={`preview-project-card-${project.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-lg font-bold text-[#0F0F0F] truncate">{project.project_name}</h3>
          <p className="text-xs text-[#687280] mt-0.5">
            {project.order_number} · {fmtDate(project.created_at)}
          </p>
        </div>
        <span
          className={`shrink-0 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${previewReady ? "bg-emerald-50 text-emerald-600" : "bg-[#D4AF37]/15 text-[#8a6d1a]"}`}
          data-testid={`project-status-${project.id}`}
        >
          {previewReady ? "Preview Live" : statusLabel(project.project_status)}
        </span>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between text-xs text-[#687280] mb-1.5">
          <span>Progress</span>
          <span className="font-semibold text-[#0F0F0F]">{pct}%</span>
        </div>
        <div className="h-2 rounded-full bg-[#F0F0F0] overflow-hidden">
          <div className="h-full bg-[#D4AF37] rounded-full transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-5 text-sm">
        <div>
          <p className="text-[#9CA3AF] text-xs">Amount paid</p>
          <p className="font-semibold text-[#0F0F0F]">${project.amount}</p>
        </div>
        <div>
          <p className="text-[#9CA3AF] text-xs">Est. delivery</p>
          <p className="font-semibold text-[#0F0F0F]">{fmtDate(project.estimated_delivery_date)}</p>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-5">
        <button
          onClick={() => navigate(`/preview-project/${project.id}`)}
          className="flex-1 inline-flex items-center justify-center gap-2 bg-[#0F0F0F] text-white rounded-full px-4 py-2.5 text-sm font-semibold hover:bg-[#1c1c1c] transition-colors"
          data-testid={`view-project-btn-${project.id}`}
        >
          View Project <ArrowRight size={15} />
        </button>
        <button
          disabled={!previewReady}
          onClick={() => previewReady && window.open(project.preview_url, "_blank", "noopener,noreferrer")}
          className="flex-1 inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors border border-gray-200 text-[#0F0F0F] enabled:hover:border-[#D4AF37] disabled:opacity-40 disabled:cursor-not-allowed"
          title={previewReady ? "Open Live Preview" : "Preview not available yet."}
          data-testid={`live-preview-btn-${project.id}`}
        >
          {previewReady ? <>Open Live Preview <ExternalLink size={14} /></> : "Preview not ready"}
        </button>
      </div>
    </div>
  );
};

export const ContinueBuildingCard = ({ project }) => {
  const navigate = useNavigate();
  const active = !!project;
  const pct = project?.progress_percentage || 0;

  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col" data-testid="continue-building-card">
      <div className="w-10 h-10 rounded-xl bg-[#0F0F0F] text-[#D4AF37] flex items-center justify-center mb-4">
        <Rocket size={20} />
      </div>
      <h3 className="font-display text-lg font-bold text-[#0F0F0F]">Continue Building Program</h3>

      {active ? (
        <>
          <p className="text-xs text-[#687280] mt-0.5 truncate">{project.project_name}</p>
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs text-[#687280] mb-1.5">
              <span>Progress</span>
              <span className="font-semibold text-[#0F0F0F]">{pct}%</span>
            </div>
            <div className="h-2 rounded-full bg-[#F0F0F0] overflow-hidden">
              <div className="h-full bg-[#D4AF37] rounded-full transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
          <button
            onClick={() => navigate("/continue-building")}
            className="mt-5 inline-flex items-center justify-center gap-2 bg-[#0F0F0F] text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-[#1c1c1c] transition-colors"
            data-testid="continue-building-view-btn"
          >
            View Program <ArrowRight size={15} />
          </button>
        </>
      ) : (
        <>
          <p className="text-sm text-[#687280] mt-1 flex-1">
            Build your app in guided monthly phases — each with an AI builder prompt, docs, project files and a checklist.
          </p>
          <button
            onClick={() => navigate("/continue-building")}
            className="mt-4 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-[#c5a030] transition-colors"
            data-testid="continue-building-start-btn"
          >
            Get Started <ArrowRight size={15} />
          </button>
        </>
      )}
    </div>
  );
};

export const ComingSoonCard = ({ icon: Icon = Rocket, title, description, onNotify, testid }) => (
  <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col" data-testid={testid}>
    <div className="w-10 h-10 rounded-xl bg-[#F7F7F7] text-[#D4AF37] flex items-center justify-center mb-4">
      <Icon size={20} />
    </div>
    <div className="inline-flex self-start items-center rounded-full bg-[#F7F7F7] text-[#687280] px-2.5 py-0.5 text-[10px] font-semibold tracking-wide mb-2">
      COMING SOON
    </div>
    <h3 className="font-display text-lg font-bold text-[#0F0F0F]">{title}</h3>
    <p className="text-sm text-[#687280] mt-1 flex-1">{description}</p>
    <button
      onClick={onNotify}
      className="mt-4 inline-flex items-center justify-center gap-2 rounded-full border border-gray-200 px-5 py-2.5 text-sm font-semibold text-[#0F0F0F] hover:border-[#D4AF37] transition-colors"
      data-testid={`${testid}-notify-btn`}
    >
      Notify Me
    </button>
  </div>
);
